import { isEqual } from 'lodash';
import { computed, ref } from 'vue';

import usePopulations from '@/composables/usePopulations';
import {
  HIDDEN,
  PARTNER_POPULATION_RULE,
  PARTNER_RULE,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { useDataSharesStore } from '@/stores';
import type { PresetOptionsGroup } from '@/types/data_share_presets';
import type { Population } from '@/types/populations';

import useSharingSettings from './useSharingSettings';
import type { DataShareItem } from './useSharingSettings';

export type FilterValues = Record<string, (string | number)[]>;

export type SubheaderItem = {
  type: 'subheader';
  title: string;
  id: string;
};

export type PopulationWithSharing = {
  population: Population;
  dataShare: DataShareItem | undefined;
  isCustom: boolean;
};

export type PopulationListItem = PopulationWithSharing | SubheaderItem;

/**
 * Population-centric composable for sharing settings
 * Extends useSharingSettings with population-focused helpers
 * Used by MyDataSharingTab
 *
 * @param partnerId - The partner organization ID
 * @returns Extended sharing settings with population-based helpers
 */
export default function useMyDataSharing(partnerId: number) {
  // Get base functionality from existing composable
  const baseComposable = useSharingSettings(partnerId);

  const {
    dataShares,
    presetOptions,
    defaultDataSharesMap,
    currentPopulationToPopulation,
    initialPopulationToPopulation,
    ...rest
  } = baseComposable;

  // Get all populations (standard + custom)
  const { standardPopulations, customPopsWithStatsAndSharing } = usePopulations(
    partnerId as never,
  );

  const allPopulations = computed(() => [
    ...standardPopulations.value,
    ...customPopsWithStatsAndSharing.value,
  ]);

  // Search and filter state
  const searchValue = ref('');
  const activeFilters = ref<FilterValues>({});

  /**
   * Get the data share for a specific population
   * @param populationId - The population ID to look up
   * @returns DataShareItem if population is shared, undefined if HIDDEN
   */
  function getDataShareForPopulation(
    populationId: number,
  ): DataShareItem | undefined {
    return dataShares.value.find((ds) => ds.populationId === populationId);
  }

  /**
   * Get visibility state for a population
   * @param populationId - The population ID to check
   * @returns Visibility type (HIDDEN if no data share exists)
   */
  function getVisibilityForPopulation(populationId: number) {
    const dataShare = getDataShareForPopulation(populationId);
    return dataShare?.visibility ?? HIDDEN;
  }

  /**
   * Get preset ID for a population
   * @param populationId - The population ID to check
   * @returns Preset ID or null if no preset assigned
   */
  function getPresetIdForPopulation(populationId: number): number | null {
    const dataShare = getDataShareForPopulation(populationId);
    return dataShare?.presetId ?? null;
  }

  /**
   * Get preset options for a specific population based on its feed/source
   * @param population - The population object
   * @returns Array of preset option groups
   */
  function getPresetOptionsForPopulation(
    population: Population,
  ): PresetOptionsGroup[] {
    const isGoogleOrCsv = isGoogleOrCsvSource(population.integration_type);
    const parentId = isGoogleOrCsv ? population.source_id : population.feed_id;
    return presetOptions.value[parentId] ?? [];
  }

  /**
   * Get the default/recommended preset ID for a population
   * Used when first sharing a population
   * @param population - The population object
   * @returns Default preset ID or null if none available
   */
  function getDefaultPresetIdForPopulation(
    population: Population,
  ): number | null {
    const options = getPresetOptionsForPopulation(population);
    const firstPreset = options[0]?.items[0];
    return firstPreset ? (firstPreset.value as number) : null;
  }

  /**
   * Get populations that are currently shared (visibility !== HIDDEN)
   */
  const sharedPopulations = computed(() =>
    allPopulations.value.filter((pop) => {
      const dataShare = getDataShareForPopulation(pop.id);
      return dataShare && dataShare.visibility !== HIDDEN;
    }),
  );

  /**
   * Get populations that are not shared (no dataShare or visibility === HIDDEN)
   */
  const unsharedPopulations = computed(() =>
    allPopulations.value.filter((pop) => {
      const dataShare = getDataShareForPopulation(pop.id);
      return !dataShare || dataShare.visibility === HIDDEN;
    }),
  );

  /**
   * Check if a population has pop-to-pop override rules for this partner.
   * Mirrors the overridesCount logic from useSharingSettingsHub.
   */
  function hasPopToPopOverrideRules(populationId: number): boolean {
    const dataSharesStore = useDataSharesStore();
    return (
      dataSharesStore.getOutgoingSharingRules({
        ruleType: PARTNER_POPULATION_RULE,
        populationId,
        partnerOrgId: partnerId,
      }).length > 0
    );
  }

  /**
   * Check if a population has custom (partner-specific) settings in the saved state
   * @param populationId - The population ID to check
   * @returns true if population has partner-specific override or pop-to-pop overrides (saved)
   */
  function hasCustomSettings(populationId: number): boolean {
    const dataSharesStore = useDataSharesStore();
    const sessionKey = dataSharesStore.getSessionKey(partnerId);

    const initialDataShare = dataSharesStore.initialEditingDataShares[
      sessionKey
    ]?.find((ds: DataShareItem) => ds.populationId === populationId);

    const hasPartnerOverride = initialDataShare?.partnerId === partnerId;

    const popToPopMap = initialPopulationToPopulation.value[populationId];
    const hasPopToPopOverrides = Boolean(
      popToPopMap && Object.keys(popToPopMap).length > 0,
    );

    const hasPartnerRule = dataSharesStore.outgoingSharingRules.some(
      (rule) =>
        rule.rule_type === PARTNER_RULE &&
        rule.partner_org_id === partnerId &&
        rule.population_id === populationId,
    );

    return hasPartnerOverride || hasPopToPopOverrides || hasPartnerRule;
  }

  /**
   * Get populations with their sharing info, separated by custom vs default
   * @param populationsToProcess - Optional list of populations to process, defaults to allPopulations
   */
  function getPopulationsWithSharing(populationsToProcess?: Population[]): {
    customPopulations: PopulationWithSharing[];
    defaultPopulations: PopulationWithSharing[];
  } {
    const customPopulations: PopulationWithSharing[] = [];
    const defaultPopulations: PopulationWithSharing[] = [];

    const populations = populationsToProcess ?? allPopulations.value;

    populations.forEach((population) => {
      const dataShare = getDataShareForPopulation(population.id);

      // Get default values for this population
      const defaultDataShare = defaultDataSharesMap.value[population.id];
      const defaultVisibility = defaultDataShare?.visibility ?? HIDDEN;
      const defaultPresetId = defaultDataShare?.presetId ?? null;

      // Get current values (including unsaved changes)
      const currentVisibility = dataShare?.visibility ?? HIDDEN;
      const currentPresetId = dataShare?.presetId ?? null;

      // Check if current values match defaults
      const matchesDefaults =
        currentVisibility === defaultVisibility &&
        currentPresetId === defaultPresetId;

      // Check if there are pop-to-pop overrides
      const popToPopMap = currentPopulationToPopulation.value[population.id];
      const hasPopToPopOverrides = Boolean(
        popToPopMap && Object.keys(popToPopMap).length > 0,
      );

      // Population is custom if:
      // - Current values don't match defaults OR
      // - Has pop-to-pop overrides
      // Note: What matters is whether the actual values differ from defaults,
      // not whether a partner override record exists in the database
      const isCustom = !matchesDefaults || hasPopToPopOverrides;

      const populationWithSharing: PopulationWithSharing = {
        population,
        dataShare,
        isCustom,
      };

      if (isCustom) {
        customPopulations.push(populationWithSharing);
      } else {
        defaultPopulations.push(populationWithSharing);
      }
    });

    return { customPopulations, defaultPopulations };
  }

  /**
   * Filters all populations based on search and active filters
   * Optimized with early returns and single-pass filtering
   */
  const filteredPopulations = computed(() => {
    const hasSearch = !!searchValue.value;
    const hasPopulationType = !!activeFilters.value.population_type?.length;
    const hasSharingLevel = !!activeFilters.value.sharing_level?.length;
    const hasFieldPresets = !!activeFilters.value.field_presets?.length;
    const hasOverrides = !!activeFilters.value.overrides?.length;

    if (
      !hasSearch &&
      !hasPopulationType &&
      !hasSharingLevel &&
      !hasFieldPresets &&
      !hasOverrides
    ) {
      return allPopulations.value;
    }

    // Pre-compute search term
    const searchLower = hasSearch ? searchValue.value.toLowerCase() : '';

    // Single-pass filter with early returns
    return allPopulations.value.filter((pop) => {
      // Search filter - fail fast
      if (hasSearch && !pop.name.toLowerCase().includes(searchLower)) {
        return false;
      }

      // Population type filter - fail fast
      if (hasPopulationType) {
        if (
          !pop.segment ||
          !activeFilters.value.population_type?.includes(pop.segment)
        ) {
          return false;
        }
      }

      // Sharing level filter - fail fast
      if (hasSharingLevel) {
        const visibility = getVisibilityForPopulation(pop.id);
        if (!activeFilters.value.sharing_level?.includes(visibility)) {
          return false;
        }
      }

      // Field presets filter - most expensive, checked last
      if (hasFieldPresets) {
        const dataShare = getDataShareForPopulation(pop.id);
        if (!dataShare?.presetId) return false;

        const presetOptions = getPresetOptionsForPopulation(pop);
        const selectedPreset = presetOptions
          .flatMap((group) => group.items)
          .find((item) => item.value === dataShare.presetId);

        if (!selectedPreset) return false;

        const matchesFilter = activeFilters.value.field_presets?.some(
          (filterValue) => {
            if (filterValue === 'crossbeam_default') {
              return selectedPreset.presetType === 'crossbeam_default';
            }
            return selectedPreset.label === filterValue;
          },
        );

        if (!matchesFilter) return false;
      }

      if (hasOverrides) {
        const isOverridden = hasPopToPopOverrideRules(pop.id);
        const wantsOverrides = activeFilters.value.overrides?.includes('true');
        const wantsNoOverrides =
          activeFilters.value.overrides?.includes('false');

        if (wantsOverrides && !isOverridden) return false;
        if (wantsNoOverrides && isOverridden) return false;
      }

      return true;
    });
  });

  /**
   * Combines custom and default populations into a single list with subheaders
   */
  function combinePopulationsWithSubheaders(
    customPopulations: PopulationWithSharing[],
    defaultPopulations: PopulationWithSharing[],
  ): PopulationListItem[] {
    const list: PopulationListItem[] = [];

    // Add custom populations section if there are any
    if (customPopulations.length > 0) {
      list.push({
        type: 'subheader',
        title: 'Custom Sharing Settings',
        id: 'subheader-custom',
      });
      list.push(...customPopulations);
    }

    // Add default populations section
    if (defaultPopulations.length > 0) {
      list.push({
        type: 'subheader',
        title: 'Default Sharing Settings',
        id: 'subheader-default',
      });
      list.push(...defaultPopulations);
    }

    return list;
  }

  /**
   * Combined list of populations with subheaders for custom vs default
   * Uses filtered populations based on search and filters
   */
  const populationsWithSubheaders = computed(() => {
    const { customPopulations, defaultPopulations } = getPopulationsWithSharing(
      filteredPopulations.value,
    );
    return combinePopulationsWithSubheaders(
      customPopulations,
      defaultPopulations,
    );
  });

  /**
   * Apply search filter
   */
  function applySearch(value: string) {
    searchValue.value = value;
  }

  /**
   * Apply filters
   */
  function applyFilters(filterValues: FilterValues) {
    activeFilters.value = filterValues;
  }

  /**
   * Clear all search and filters
   */
  function clearFilters() {
    searchValue.value = '';
    activeFilters.value = {};
  }

  /**
   * Check if a specific population has unsaved changes
   * Compares current data share with initial data share from the store
   * Also checks for population-to-population override changes
   */
  function hasPopulationChanges(populationId: number): boolean {
    const dataSharesStore = useDataSharesStore();
    const sessionKey = dataSharesStore.getSessionKey(partnerId);

    // Check for data share changes
    const currentDataShare = dataShares.value.find(
      (ds) => ds.populationId === populationId,
    );

    const initialDataShare = dataSharesStore.initialEditingDataShares[
      sessionKey
    ]?.find((ds: DataShareItem) => ds.populationId === populationId);

    if (!currentDataShare !== !initialDataShare) {
      return true;
    }

    if (currentDataShare && initialDataShare) {
      // Compare only the meaningful fields that represent the user's intent
      // This allows reverting default -> custom -> default to correctly show no changes
      const hasDataShareChanges =
        currentDataShare.visibility !== initialDataShare.visibility ||
        currentDataShare.presetId !== initialDataShare.presetId;

      if (hasDataShareChanges) {
        return true;
      }
    }

    // Check for population-to-population override changes
    const currentOverrides =
      currentPopulationToPopulation.value[populationId] || {};
    const initialOverrides =
      initialPopulationToPopulation.value[populationId] || {};

    return !isEqual(currentOverrides, initialOverrides);
  }

  // Return all functionality from base composable plus new helpers
  return {
    ...rest,
    dataShares,
    presetOptions,
    defaultDataSharesMap,
    currentPopulationToPopulation,
    allPopulations,
    sharedPopulations,
    unsharedPopulations,
    filteredPopulations,
    populationsWithSubheaders,
    getDataShareForPopulation,
    getVisibilityForPopulation,
    getPresetIdForPopulation,
    getPresetOptionsForPopulation,
    getDefaultPresetIdForPopulation,
    hasCustomSettings,
    hasPopulationChanges,
    // Search and filters
    searchValue,
    activeFilters,
    applySearch,
    applyFilters,
    clearFilters,
  };
}
